import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, Pencil, BookOpen } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const empty = { question: "", answer: "", enabled: true };

export const KnowledgeBaseEditor = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/assistant/kb");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (it) => { setEditing(it.id); setForm({ question: it.question, answer: it.answer, enabled: it.enabled }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/assistant/kb/${editing}`, form);
      else await api.post("/admin/assistant/kb", form);
      toast.success("Knowledge saved — the assistant will use it right away.");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (it) => {
    try {
      await api.put(`/admin/assistant/kb/${it.id}`, { ...it, enabled: !it.enabled });
      setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, enabled: !x.enabled } : x)));
    } catch (err) { toast.error("Could not update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this knowledge entry?")) return;
    try { await api.delete(`/admin/assistant/kb/${id}`); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="kb-editor">
      <div className="mb-4 flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm text-white/70"><BookOpen size={16} /> Custom facts & FAQs the AI Assistant will use when answering customers.</p>
        <button onClick={openNew} data-testid="kb-new-btn" className="inline-flex items-center gap-2 rounded-full bg-brandred px-4 py-2 text-sm font-bold text-white hover:bg-brandred-700">
          <Plus size={15} /> Add Knowledge
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-white" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-muted-foreground">No knowledge entries yet. Add FAQs, prices, policies, or facts the AI should know.</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`kb-item-${it.id}`} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-navy">{it.question}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{it.answer}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button onClick={() => toggle(it)} data-testid={`kb-toggle-${it.id}`} className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${it.enabled ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"}`}>
                    {it.enabled ? "Active" : "Off"}
                  </button>
                  <button onClick={() => openEdit(it)} data-testid={`kb-edit-${it.id}`} className="rounded-lg border border-gray-200 p-1.5 text-navy hover:bg-navy-50"><Pencil size={15} /></button>
                  <button onClick={() => remove(it.id)} data-testid={`kb-delete-${it.id}`} className="rounded-lg border border-red-200 p-1.5 text-brandred hover:bg-red-50"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10">
          <form onSubmit={save} data-testid="kb-form" className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-black text-navy">{editing ? "Edit" : "Add"} Knowledge</h2>
              <button type="button" onClick={() => setShowForm(false)}><X className="text-gray-400" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Question / Topic *</label>
                <input required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="kb-question"
                  placeholder="e.g. How long does RoRo shipping take?" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-navy outline-none focus:border-navy" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Answer / Fact *</label>
                <textarea required rows={4} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="kb-answer"
                  placeholder="e.g. RoRo from Newark to Tema takes about 4-6 weeks." className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-navy outline-none focus:border-navy" />
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="kb-enabled" /> Active (used by the assistant)
              </label>
            </div>
            <button type="submit" disabled={saving} data-testid="kb-save-btn" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brandred px-6 py-3 text-sm font-bold text-white hover:bg-brandred-700 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing ? "Save Changes" : "Add Knowledge"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
